import React from "react";
import { useLoadGoogleFontFamily } from "./useLoadGoogleFontFamily";
import { SkeletonTextLoader } from "components/skeleton/SkeletonTextLoader";
import styles from "./index.module.scss";

interface FontFamilySelectProps {
    label?: string;
    id: string;
    value: string | undefined;
    error?: string;
    help?: string;
    disabled?: boolean;
    onChange?: (event: React.ChangeEvent<HTMLSelectElement>) => void;
}

export const FontFamilySelect = ({
    label,
    id,
    value,
    help,
    disabled = false,
    onChange
}: FontFamilySelectProps) => {
    const { availableFonts, loading, ready, error } = useLoadGoogleFontFamily({
        capability: "WOFF2",
        category: "monospace",
        additionalFonts: ["Noto Sans"],
        excludeFonts: [
            "M PLUS 1 Code",
            "Material Icons",
            "Material Icons Outlined",
            "Material Icons Round",
            "Material Icons Two Tone",
            "Material Icons Sharp",
            "Material Symbols",
            "Material Symbols Outlined",
            "Material Symbols Rounded",
            "Material Symbols Sharp"
        ]
    });

    if (!ready && loading) {
        return (
            <div className="form-group">
                <label className={styles["label"]} htmlFor={id}>
                    {label}
                </label>
                <hr className={styles["hr"]} />
                <div>
                    <SkeletonTextLoader />
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="form-group">
                <label className={styles["label"]} htmlFor={id}>
                    {label}
                </label>
                <hr className={styles["hr"]} />
                <div>
                    {!error && (
                        <select
                            className={styles["font-select"]}
                            id={id}
                            value={value || ""}
                            onChange={onChange}
                            disabled={disabled}
                            style={{
                                fontFamily: `${value}, sans-serif` || "inherit"
                            }}
                        >
                            {availableFonts.map(({ family }) => (
                                <option
                                    key={family}
                                    value={family}
                                    style={{
                                        fontFamily:
                                            `${family}, sans-serif` || "inherit"
                                    }}
                                >
                                    {family}
                                </option>
                            ))}
                        </select>
                    )}

                    <small
                        id={`${id}-help`}
                        aria-describedby={id}
                        className={`form-text ${
                            error ? "text-danger" : "text-muted"
                        }`}
                    >
                        {error?.message ?? help}
                    </small>
                </div>
            </div>
        </>
    );
};
